import React, { memo } from 'react';
import { View } from 'react-native';

import { EventInfo } from '@/api/events/types/types';
import { betslipPlayerSelector, useBetslipStore } from '@/feature/betslip-pickem/hooks/use-betslip-store';
import { playerPropsSelector, usePlayerPropsStore } from '@/feature/betslip-pickem/hooks/use-player-props-store';
import { PlayerWithTeam } from '@/feature/betslip-pickem/types';
import { PlayerRow } from '@/feature/lobby/components/PlayerRow';
import { PickSelectionMethods } from '@/feature/lobby/hooks/use-pick-selection';
import { FilterType, Filters } from '@/feature/lobby/utils/filters';
import { isSameProjection } from '@/utils/projections';
import { useShallow } from 'zustand/react/shallow';

type Props = {
    eventInfo: EventInfo;
    player: PlayerWithTeam;
    filter: FilterType;
    testID?: string;
    analyticsTag?: string;
    hideGameTime?: boolean;
    openPlayerProfile: (playerId: string, eventId: string) => void;
} & PickSelectionMethods;

/**
 * Represents a Player Row
 */
export const PlayerItem = memo(
    ({
        eventInfo,
        player,
        filter = Filters.Position,
        testID,
        analyticsTag,
        removeSelection,
        makeSelection,
        hideGameTime,
        openPlayerProfile,
    }: Props) => {
        const playerProjections = usePlayerPropsStore(useShallow(playerPropsSelector(player.id, eventInfo.id)));
        const playerPickedEntry = useBetslipStore(
            betslipPlayerSelector({
                eventId: eventInfo.id,
                playerId: player.id,
            })
        );

        /**
         * Find the projection that user is filtering on
         */
        const projection = playerProjections[0];

        const isThisProjectionPicked = playerPickedEntry && isSameProjection(playerPickedEntry.projection, projection);

        const playerRowProps = isThisProjectionPicked
            ? { mode: 'selection' as const, projection, outcome: playerPickedEntry.outcome }
            : { mode: 'highlight' as const, projection };

        return (
            <View testID={`listedPlayer-${testID}`}>
                <React.Fragment key={`player-${player.id}-${eventInfo.id}-${projection?.key}`}>
                    {filter === Filters.Projection ? (
                        <PlayerRow
                            player={player}
                            event={eventInfo}
                            {...playerRowProps}
                            pressable
                            testID={testID}
                            removeSelection={removeSelection}
                            makeSelection={makeSelection}
                            analyticsTag={analyticsTag}
                            hideGameTime={hideGameTime}
                            openPlayerProfile={openPlayerProfile}
                        />
                    ) : playerPickedEntry ? (
                        <PlayerRow
                            player={player}
                            projection={playerPickedEntry.projection}
                            mode={'selection'}
                            event={eventInfo}
                            outcome={playerPickedEntry.outcome}
                            pressable
                            testID={testID}
                            removeSelection={removeSelection}
                            makeSelection={makeSelection}
                            analyticsTag={analyticsTag}
                            hideGameTime={hideGameTime}
                            openPlayerProfile={openPlayerProfile}
                        />
                    ) : (
                        <PlayerRow
                            player={player}
                            mode={'all'}
                            event={eventInfo}
                            pressable
                            testID={testID}
                            removeSelection={removeSelection}
                            makeSelection={makeSelection}
                            analyticsTag={analyticsTag}
                            hideGameTime={hideGameTime}
                            openPlayerProfile={openPlayerProfile}
                        />
                    )}
                </React.Fragment>
            </View>
        );
    }
);
